"use client"

import { useState } from "react"
import { User } from "lucide-react"
import { AnimatedSection } from "./AnimatedSection"
import { Card, CardContent } from "./Card"
import { FallbackImage } from "./FallbackImage"

// LinkedIn logo (your existing one)
function LinkedInLogoIcon({ className = "" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.25 6.5 1.75 1.75 0 016.5 8.25zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.62 1.62 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 4.12z"></path>
    </svg>
  )
}

// YouTube logo (custom SVG)
function YouTubeLogoIcon({ className = "" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 333333 333333" shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" className={className}>
      <path d="M329930 100020s-3254-22976-13269-33065c-12691-13269-26901-13354-33397-14124-46609-3396-116614-3396-116614-3396h-122s-69973 0-116608 3396c-6522 793-20712 848-33397 14124C6501 77044 3316 100020 3316 100020S-1 126982-1 154001v25265c0 26962 3315 53979 3315 53979s3254 22976 13207 33082c12685 13269 29356 12838 36798 14254 26685 2547 113354 3315 113354 3315s70065-124 116675-3457c6522-770 20706-848 33397-14124 10021-10089 13269-33090 13269-33090s3319-26962 3319-53979v-25263c-67-26962-3384-53979-3384-53979l-18 18-2-2zM132123 209917v-93681l90046 46997-90046 46684z" fill="red"/>
    </svg>
  )
}

// Extract a YouTube video id from most common URL shapes
function getYouTubeId(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1);
    const v = u.searchParams.get("v");
    if (v) return v;
    const parts = u.pathname.split("/").filter(Boolean);
    const i = parts.findIndex(p => p === "embed" || p === "shorts");
    if (i !== -1 && parts[i + 1]) return parts[i + 1];
  } catch {}
  return "";
}


export function SpeakersSection({ currentEdition = "vizag" }) {
  const [openVideoId, setOpenVideoId] = useState(null);

  const openVideo = (youtube) => {
    const id = getYouTubeId(youtube);
    if (id) setOpenVideoId(id);
    else if (youtube) window.open(youtube, "_blank", "noopener,noreferrer");
  };

  // Vizag Edition Speakers
  const vizagSpeakers = [
    {
      name: "Susinder Gulasekaran",
      company: "Swinnovate",
      topic: "The Problems with Spatial reuse in Wi-Fi 6 & Wi-Fi 7",
      image: "/images/Susinder.png",
      linkedin: "https://www.linkedin.com/in/susinder",
      youtube: "https://youtu.be/EEy9GaDtb5w",
    },
    {
      name: "Dr. Srikanth Subramanian",
      company: "Nanocell Networks",
      topic: "802.11 PHY Evolution, Hits and Misses",
      image: "/images/srikanth.png",
      linkedin: "https://in.linkedin.com/in/srikanth-s-6b0b303",
      youtube: "https://youtu.be/cZFWRNEGJ_c",
    },
    {
      name: "Rasika Nayanajith",
      company: "Independent Expert",
      topic: "Exploring Wi-Fi 7 Security Enhancements",
      image: "/images/rasika.png",
      linkedin: "https://au.linkedin.com/in/rasikanayanajith",
      youtube: "https://youtu.be/-f46TFM6cHk",
    },
    {
      name: "Shashank Tadakamadla",
      company: "Cambium Networks",
      topic: "Real Time WiFi Testing - Providing Immediate Stability & Behavioural Insights",
      image: "/images/sashank.png",
      linkedin: "https://in.linkedin.com/in/shashanktadakamadla",
      youtube: "https://youtu.be/u4mHRaMdHIE",
    },
    {
      name: "Gjermund Raaen",
      company: "Independent Expert",
      topic: "Understanding EHT Preamble in WiFi 7 Phy Layer",
      image: "/images/Gjermund Raaen.png",
      linkedin: "https://no.linkedin.com/in/gjermund-raaen-954b56265",
      youtube: "https://youtu.be/jXKOhQfgR1w",
    },
    {
      name: "Bhuvnesh Sachdeva",
      company: "HFCL",
      topic: "Role of AI in Wi-Fi",
      image: "/images/bhuvnesh.png",
      linkedin: "https://www.linkedin.com/in/bsach/",
      youtube: "https://youtu.be/HokLLQF78Rc",
    },
    {
      name: "Shivam Thakur",
      company: "Plume Design, Inc",
      topic: "Nuts and Bolts of Wi-Fi and OpenWrt",
      image: "/images/shivam.png",
      linkedin: "https://in.linkedin.com/in/shivam-thakur-49978016a",
      youtube: "https://youtu.be/OVD1UJfEOi8",
    },
  ]

  // Bangalore Edition Speakers
  const bangaloreSpeakers = [
    {
      name: "Dr. Srikanth Subramanian",
      company: "Nanocell Networks",
      topic: "WiFi and IOT : Is there a match",
      image: "/images/srikanth.png",
      linkedin: "https://in.linkedin.com/in/srikanth-s-6b0b303",
      youtube: "https://www.youtube.com/watch?v=QY8IE2LjZxM",
    },
    {
      name: "Susinder Gulasekaran",
      company: "Swinnovate",
      topic: "Testing OFDMA Benefits",
      image: "/images/Susinder.png",
      linkedin: "https://www.linkedin.com/in/susinder",
      youtube: "https://youtu.be/JoMey0wKbd4?si=UUcKzKEKLZrnPJ7w",
    },
    {
      name: "Asvin Kumar",
      company: "Arista Networks",
      topic: "Exploring T2LM in Wi-Fi 7: Optional Now, Practical Benefit for Admins (When Implemented Correctly)",
      image: "/images/Asvin.png",
      linkedin: "https://www.linkedin.com/in/asvin-kumar-0850851/",
      youtube: "https://youtu.be/4uPNIzfWiAo?si=HR8VlyVa4t9GW9aX",
    },
    {
      name: "Charumati Iyer",
      company: "C-DOT",
      topic: "Fixed Wireless Access (FWA): Architecture, Technologies, and Evolution Towards 5G and Beyond",
      image: "/images/Charumati.png",
      linkedin: "https://www.linkedin.com/in/charumati-iyer-1a7aa39a/",
      youtube: "https://youtu.be/wwG_j8Uto4M?si=ra1zN-L40UiKc6_w",
    },
    {
      name: "Sheela C S",
      company: "IISc Bangalore",
      topic: "Intelligent Cross-Layer Rate Adaptation in IEEE 802.11ax WLANs using Bayesian Learning Mechanisms",
      image: "/images/Sheela.png",
      linkedin: "https://www.linkedin.com/in/sheela-c-s-67671742/",
      youtube: "https://youtu.be/BFvEQhxgCAE?si=3CvVE_6mY41bLBNe",
    },
    {
      name: "Ravi Srinivasan",
      company: "Reliance Jio",
      topic: "WiFi in Indian homes : An ISP perspective",
      image: "/images/Ravishankar.png",
      linkedin: "https://www.linkedin.com/in/ravishankarsrini/",
      youtube: "https://youtu.be/Z6hIAHzsOwE?si=bFJ7baXXUk92GCJm",
    },

    {
      name: "Mayur Sarode",
      company: "Solace Power",
      topic: "Demystifying RF in Wi-Fi: A System-Level Look at the PHY Layer",
      image: "/images/Mayur.png",
      linkedin: "https://www.linkedin.com/in/mayur-sarode/",
      youtube: "https://youtu.be/GMzKfm8Cz6o?si=RcDCXwWtmmB226wq",
    },
    {
      name: "Nadeem Akhtar",
      company: "HFCL",
      topic: "Secure Wireless Access in Enterprise Networks",
      image: "/images/Nadeem.png",
      linkedin: "https://www.linkedin.com/in/nadeem-akhtar-bbb0615",
      youtube: "https://youtu.be/kT_m8JnFtfQ?si=FanSw8vhmKqVZQNi",
    },
    {
      name: "Prabhash Dhyani",
      company: "Independent",
      topic: "Panel Discussion: The Role of AI in Modern Wi-Fi Networks",
      image: "/images/Prabhash.png",
      linkedin: "https://www.linkedin.com/in/prabhashdhyani/",
      youtube: "https://youtu.be/nBHmlnz5Lsg?si=n5cgoCMXPECNBo6r",
    },
    {
      name: "Ashutosh Datta",
      company: "HFCL",
      topic: "Panel Discussion: The Role of AI in Modern Wi-Fi Networks",
      image: "/images/Ashutosh.png",
      linkedin: "https://www.linkedin.com/in/ashutoshdatta/",
      youtube: "https://youtu.be/nBHmlnz5Lsg?si=n5cgoCMXPECNBo6r",
    },
    {
      name: "Gopi Raga",
      company: "Amazon",
      topic: "Wi-Fi Tools: Airtime Calculator and SSID Overhead Calculator",
      image: "/images/Gopi.png",
      linkedin: "https://www.linkedin.com/in/gopi-raga-98b852176/",
      youtube: "https://youtu.be/fRQ5hhcTiDI?si=kKhXOvVA15V9eGwp",
    },
  ]

  // Chennai Edition 2026 Speakers
  const chennaiSpeakers = [
    {
      name: "Susinder Gulasekaran",
      company: "Swinnovate",
      image: "/images/Susinder.png",
      linkedin: "https://www.linkedin.com/in/susinder",
    },
    {
      name: "Rasika Nayanajith",
      company: "Independent Expert",
      image: "/images/rasika.png",
      linkedin: "https://au.linkedin.com/in/rasikanayanajith",
    },
    {
      name: "Dr. Srikanth Subramanian",
      company: "Nanocell Networks",
      image: "/images/srikanth.png",
      linkedin: "https://in.linkedin.com/in/srikanth-s-6b0b303",
    },
    {
      name: "Babu Subash Chandar C",
      company: "Zilogic Systems",
      image: "/images/BabuSubash.png",
      linkedin: "https://www.linkedin.com/in/babuenir",
    },
    {
      name: "Shivam Thakur",
      company: "Hewlett Packard Enterprise (Juniper)",
      image: "/images/shivam.png",
      linkedin: "https://in.linkedin.com/in/shivam-thakur-49978016a",
    },
    {
      name: "Tushar Nautiyal",
      company: "HFCL",
      image: "/images/tushar.png",
      linkedin: "https://www.linkedin.com/in/tusshar-nautiyal/",
    },
    {
      name: "Yamini Shankar",
      company: "IIT Madras",
      image: "/images/yamini.png",
      linkedin: "https://www.linkedin.com/in/yamini-shankar-a6379b134/",
    },
    {
      name: "Sudarsan Vasudevan",
      company: "Netgear",
      image: "/images/sudarshan.png",
      linkedin: "https://www.linkedin.com/in/sudavasu/",
    },
    {
      name: "Dr. Vijayalakshmi Chetlapalli",
      company: "Arista",
      image: "/images/vijayalakshmi.png",
      linkedin: "https://www.linkedin.com/in/vijayalakshmi-chetlapalli-7524ba5/",
    },
    {
      name: "Prem Thilagar",
      company: "Qualcomm",
      image: "/images/premthilagar.png",
      linkedin: "https://www.linkedin.com/in/prem-thilagar-2758817/",
    },
    {
      name: "Srihari Venkiteswaran",
      company: "Hewlett Packard Enterprise (Aruba)",
      image: "/images/srihari.png",
      linkedin: "https://www.linkedin.com/in/srihari-venkiteswaran-b48816a/",
    },
    {
      name: "Sruthik Issac",
      company: "Hewlett Packard Enterprise (Aruba)",
      image: "/images/sruthik.png",
      linkedin: "https://www.linkedin.com/in/sruthik-issac/",
    },
    {
      name: "Pradeep Reddy",
      company: "Morse Micro",
      image: "/images/pradeepreddy.png",
      linkedin: "https://www.linkedin.com/in/pradeep-bhureddy/",
    },
    {
      name: "Vishnu Kumar",
      company: "WiFi Nomads",
      image: "/images/vishnu.png",
      linkedin: "https://www.linkedin.com/in/vishnu435/",
    },
    {
      name: "Surya Prakash",
      company: "WiFi Nomads",
      image: "/images/surya.png",
      linkedin: "https://www.linkedin.com/in/surya-prakash-mullangi/",
    },
  ]

  // Select speakers based on edition
  const speakers =
    currentEdition === "bangalore"
      ? bangaloreSpeakers
      : currentEdition === "chennai"
        ? chennaiSpeakers
        : vizagSpeakers

  // Small helper so we don't repeat the same card markup twice
  const SpeakerCard = (speaker, key) => (
    <AnimatedSection key={key} delay={key * 150} className="h-full">
      <Card className="group relative hover:shadow-xl transition-all duration-300 border-green-100 hover:border-green-200 bg-white/90 backdrop-blur-sm overflow-hidden h-full flex flex-col">
        {(speaker.linkedin || speaker.youtube) && (
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            {speaker.youtube && (
              <button
                type="button"
                onClick={() => openVideo(speaker.youtube)}
                aria-label={`Play ${speaker.name}'s talk on YouTube`}
                className="text-red-600 hover:text-red-700 focus:outline-none"
                title="Watch on YouTube"
              >
                <YouTubeLogoIcon className="w-6 h-6" />
              </button>
            )}
            {speaker.linkedin && speaker.linkedin !== "#" && (
              <a
                href={speaker.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${speaker.name}'s LinkedIn Profile`}
                className="text-[#0077B5] hover:opacity-90"
                title="Open LinkedIn"
              >
                <LinkedInLogoIcon className="w-6 h-6" />
              </a>
            )}
          </div>
        )}

        <CardContent className="p-6 pt-10 text-center flex flex-col h-full">
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="relative mb-6">
              <div className="w-32 h-40 mx-auto mb-4 relative">
                <FallbackImage
                  src={speaker.image || "/placeholder.svg"}
                  alt={speaker.name}
                  className="w-full h-full object-cover rounded-lg border-2 border-green-200 group-hover:border-green-400 transition-colors shadow-lg"
                />
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center shadow-lg">
                  <User className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>

            {speaker.linkedin && speaker.linkedin !== "#" ? (
              <a href={speaker.linkedin} target="_blank" rel="noopener noreferrer">
                <h3 className="font-bold text-lg text-green-800 mb-1 group-hover:text-green-600 transition-colors">
                  {speaker.name}
                </h3>
              </a>
            ) : (
              <h3 className="font-bold text-lg text-green-800 mb-1 group-hover:text-green-600 transition-colors">
                {speaker.name}
              </h3>
            )}

            <p className="font-semibold text-sm text-slate-500 mb-3">{speaker.company}</p>
            {speaker.topic && (
              <p className="text-green-600 font-medium mb-4 text-sm flex-grow flex items-center justify-center">
                {speaker.topic}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </AnimatedSection>
  )

  return (
    <section id="speakers" className="py-20 bg-gradient-to-br from-green-50 to-white">
      <div className="container mx-auto px-4">
        <AnimatedSection>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Speakers</h2>
            <p className="text-xl text-green-600 max-w-2xl mx-auto">
              Learn from industry experts and thought leaders in wireless technology
            </p>
          </div>
        </AnimatedSection>

        <div className="max-w-7xl mx-auto">
          {/* Row 1: first 4 - centered for short editions */}
          <div className={speakers.length < 4 ? "flex justify-center" : ""}>
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-8 ${speakers.length < 4 ? "lg:grid-cols-3 max-w-5xl" : "lg:grid-cols-4"}`}>
              {speakers.slice(0, 4).map((s, i) => SpeakerCard(s, i))}
            </div>
          </div>

          {/* Row 2: next 4 speakers - centered if less than 4, same card width as row 1 */}
          {speakers.length > 4 && speakers.length <= 8 && speakers.slice(4, 8).length < 4 ? (
            <div className="mt-8">
              <div className="flex flex-wrap justify-center gap-8">
                {speakers.slice(4, 8).map((s, i) => (
                  <div key={i + 4} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)]">
                    {SpeakerCard(s, i + 4)}
                  </div>
                ))}
              </div>
            </div>
          ) : speakers.length > 4 && (
            <div className="mt-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {speakers.slice(4, 8).map((s, i) => SpeakerCard(s, i + 4))}
              </div>
            </div>
          )}

          {/* Row 3: next 4 speakers (9-12) - centered, same card width as rows above */}
          {speakers.length > 8 && (
            <div className="mt-8">
              <div className="flex flex-wrap justify-center gap-8">
                {speakers.slice(8, 12).map((s, i) => (
                  <div key={i + 8} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)]">
                    {SpeakerCard(s, i + 8)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Row 4: remaining speakers (13+) if any - centered, same card width as rows above */}
          {speakers.length > 12 && (
            <div className="mt-8">
              <div className="flex flex-wrap justify-center gap-8">
                {speakers.slice(12).map((s, i) => (
                  <div key={i + 12} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)]">
                    {SpeakerCard(s, i + 12)}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

      </div>

      {/* Lightweight modal player */}
      {openVideoId && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
          onClick={() => setOpenVideoId(null)}
        >
          <div
            className="relative w-full max-w-4xl aspect-video bg-black rounded-xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setOpenVideoId(null)}
              aria-label="Close video"
              className="absolute -top-10 right-0 text-white/90 hover:text-white"
            >
              ✕
            </button>
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${openVideoId}?autoplay=1`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  )
}
